// One shared view of the data-feed catalogue's health. AppShell calls this
// once (60 s poll) and hands the result to the header status dot and the
// staleness strip, so the console makes a single feed-health request per
// minute instead of one per widget.

import { useEffect, useMemo } from "react";
import { useQuery } from "urql";

import { DataFeedsDocument, type DataFeedStatus } from "@/app/queriesData";

const POLL_MS = 60_000;

/** green = every feed live · amber = some stale/partial/idle · red = any offline */
export type SystemStatus = "live" | "stale" | "outage" | "unknown";

export interface FeedHealth {
  feeds: DataFeedStatus[];
  live: number;
  stale: DataFeedStatus[];
  offline: DataFeedStatus[];
  /** stale ∪ offline — what the staleness strip counts. */
  unhealthy: DataFeedStatus[];
  status: SystemStatus;
  fetching: boolean;
  error: string | null;
}

export function useFeedHealth(): FeedHealth {
  const [{ data, fetching, error }, reexecute] = useQuery({
    query: DataFeedsDocument,
    requestPolicy: "cache-and-network",
  });

  useEffect(() => {
    const id = window.setInterval(() => reexecute({ requestPolicy: "network-only" }), POLL_MS);
    return () => window.clearInterval(id);
  }, [reexecute]);

  const feeds = useMemo<DataFeedStatus[]>(() => data?.dataFeeds ?? [], [data?.dataFeeds]);

  return useMemo(() => {
    const live = feeds.filter((f) => f.status === "live").length;
    const offline = feeds.filter((f) => f.status === "offline");
    const stale = feeds.filter((f) => f.status === "stale");
    const unhealthy = feeds.filter((f) => f.status === "stale" || f.status === "offline");
    let status: SystemStatus = "unknown";
    if (error && feeds.length === 0) status = "unknown";
    else if (offline.length > 0) status = "outage";
    else if (feeds.length > 0 && live < feeds.length) status = "stale";
    else if (feeds.length > 0) status = "live";
    return {
      feeds,
      live,
      stale,
      offline,
      unhealthy,
      status,
      fetching,
      error: error ? error.message : null,
    };
  }, [feeds, fetching, error]);
}
